import React, { useCallback } from 'react'
import SeedCropDetailsForm, { FormData } from './SeedCropDetailsForm/SeedCropDetailsForm'
import * as Layout from 'views/layouts/MainLayout/MainLayout'
import * as Header from 'views/ui/Header/Header'
import { useSeedCrop } from 'modules/domain/seedCrop/hooks'
import { useParams } from 'react-router-dom'
import { Progress } from 'modules/types'
import { useSelector } from 'react-redux'
import SeedCropSelectors from 'modules/domain/seedCrop/selectors'
import SeedCropActions from 'modules/domain/seedCrop/duck'
import Routes from './routes'
import { useTranslation } from 'react-i18next'
import ItemLoadingLayout from 'views/layouts/ItemLoadingLayout/ItemLoadingLayout'
import Item404 from 'views/layouts/Item404/Item404'
import ItemGenericError from 'views/layouts/ItemGenericError/ItemGenericError'
import { useAction, useHelmet, useHistoryPush } from '@agro-club/frontend-shared'

const SeedCropEdit: React.FC = () => {
  const params = useParams<{ id: string }>()
  const push = useHistoryPush()
  const updateSeedCropAction = useAction(SeedCropActions.updateRequested)
  const removeSeedCropAction = useAction(SeedCropActions.removeRequested)
  const page = useSelector(SeedCropSelectors.page)
  const { t } = useTranslation('seedCrop')

  const handleSubmit = useCallback(
    (data: FormData) => {
      updateSeedCropAction(params.id, {
        name: data.name,
      })
    },
    [params.id, updateSeedCropAction],
  )

  const handleRemove = () => {
    removeSeedCropAction(params.id)
  }

  const [fetchProgress, seedCrop] = useSeedCrop(params.id)

  const meta = useSelector((state) => SeedCropSelectors.meta(state, params.id))

  const goBack = () => push({ route: Routes.List, query: { page } })

  useHelmet({ title: t('seedCropsMetaTitle') })

  const loading = () => <ItemLoadingLayout id={params.id} onBack={goBack} />
  const error404 = () => <Item404 id={params.id} onBack={goBack} title={t('errors.notFoundTitle')} />
  const errorUnknown = () => <ItemGenericError id={params.id} onBack={goBack} title={t('errors.unknownErrorTitle')} />

  if (fetchProgress === Progress.ERROR) {
    if (meta.fetchError === 'not_found') {
      return error404()
    }
    return errorUnknown()
  }

  if (fetchProgress === Progress.WORK || !seedCrop) {
    return loading()
  }

  return (
    <>
      <Layout.Header>
        <Header.Root onClickBack={goBack}>
          <Header.Title size={'small'} title={seedCrop.name} />
        </Header.Root>
      </Layout.Header>
      <Layout.Content>
        <SeedCropDetailsForm
          onCancel={goBack}
          onRemove={handleRemove}
          mode={'edit'}
          progress={meta.updateProgress}
          removeProgress={meta.removeProgress}
          initialValues={{
            name: seedCrop.name || '',
          }}
          onSubmit={handleSubmit}
        />
      </Layout.Content>
    </>
  )
}

export default SeedCropEdit
